import React from "react";
import { toast } from "sonner";
import { Save, RotateCcw, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { CONTENT_SCHEMA, getByPath, setByPath } from "../../lib/contentSchema";
import { translations, useLang } from "../../contexts/LangContext";
import { api, formatErrorDetail } from "../../lib/api";

// Convert array of strings <-> textarea text (one item per non-empty line)
function listToText(arr) {
  if (!Array.isArray(arr)) return "";
  return arr.filter((s) => typeof s === "string").join("\n");
}
function textToList(text) {
  return (text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function ContentEditor() {
  const { refreshContent } = useLang();
  const [overrides, setOverrides] = React.useState({ it: {}, en: {} });
  const [loaded, setLoaded] = React.useState(false);
  const [openSections, setOpenSections] = React.useState(() => new Set([CONTENT_SCHEMA[0].key]));
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const ov = data?.content_overrides || {};
      setOverrides({ it: ov.it || {}, en: ov.en || {} });
    } catch (e) {
      toast.error("Errore caricamento contenuti");
    } finally {
      setLoaded(true);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    setOpenSections((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Effective value: override (if set) else default from translations
  const valueFor = (lang, path) => {
    const o = getByPath(overrides[lang], path);
    if (o !== undefined) return o;
    return getByPath(translations[lang], path);
  };

  const setOverride = (lang, path, value) => {
    setOverrides((cur) => ({ ...cur, [lang]: setByPath(cur[lang] || {}, path, value) }));
  };

  const resetOverride = (path) => {
    // remove path from both languages overrides — but easier: set to default value
    setOverride("it", path, getByPath(translations.it, path));
    setOverride("en", path, getByPath(translations.en, path));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/settings/content_overrides`, { value: overrides });
      toast.success("Contenuti salvati. Aggiorna il sito per vederli.");
      refreshContent();
    } catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Ripristinare TUTTI i testi predefiniti? Le tue modifiche verranno perse.")) return;
    setOverrides({ it: {}, en: {} });
    try {
      await api.put(`/admin/settings/content_overrides`, { value: { it: {}, en: {} } });
      toast.success("Contenuti ripristinati");
      refreshContent();
    } catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail));
    }
  };

  if (!loaded) return <div className="text-[color:var(--txt-secondary)]">Caricamento...</div>;

  return (
    <div data-testid="admin-content-editor">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="content-save">
          <Save className="w-4 h-4" /> {saving ? "Salvataggio..." : "Salva tutti i contenuti"}
        </button>
        <button onClick={resetAll} className="btn-ghost hover:!border-[color:var(--fire)] hover:!text-[color:var(--fire)]">
          <RotateCcw className="w-4 h-4" /> Ripristina default
        </button>
        <span className="text-xs text-[color:var(--txt-muted)]">
          Ogni campo offre la versione italiana e quella inglese. I testi non modificati usano i valori predefiniti.
        </span>
      </div>

      <div className="space-y-3">
        {CONTENT_SCHEMA.map((section) => (
          <div key={section.key} className="border border-white/10">
            <button
              type="button"
              onClick={() => toggle(section.key)}
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/[0.02] transition-colors"
              data-testid={`content-section-${section.key}`}
            >
              <span className="font-display font-semibold text-white">{section.title}</span>
              {openSections.has(section.key) ? (
                <ChevronUp className="w-5 h-5 text-[color:var(--gold)]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-white/40" />
              )}
            </button>

            {openSections.has(section.key) && (
              <div className="px-6 pb-6 pt-2 space-y-7 border-t border-white/5">
                {section.fields.map((f) => (
                  <FieldRow
                    key={f.path}
                    field={f}
                    valueIT={valueFor("it", f.path)}
                    valueEN={valueFor("en", f.path)}
                    onChangeIT={(v) => setOverride("it", f.path, v)}
                    onChangeEN={(v) => setOverride("en", f.path, v)}
                    onReset={() => resetOverride(f.path)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex">
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Salvataggio..." : "Salva tutti i contenuti"}
        </button>
      </div>
    </div>
  );
}

function FieldRow({ field, valueIT, valueEN, onChangeIT, onChangeEN, onReset }) {
  return (
    <div data-testid={`field-${field.path.replace(/\./g, "-")}`}>
      <div className="flex items-end justify-between gap-3 mb-2">
        <label className="font-display text-[11px] tracking-[0.18em] uppercase text-[color:var(--gold)]">
          {field.label}
          <span className="ml-2 text-white/40 normal-case tracking-normal text-[10px]">[{field.path}]</span>
        </label>
        <button
          type="button"
          onClick={onReset}
          className="text-[10px] text-white/50 hover:text-[color:var(--gold)] underline underline-offset-4"
          title="Ripristina valore predefinito"
        >
          ripristina
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LangCell flag="🇮🇹" lang="IT" type={field.type} value={valueIT} onChange={onChangeIT} />
        <LangCell flag="🇬🇧" lang="EN" type={field.type} value={valueEN} onChange={onChangeEN} />
      </div>
    </div>
  );
}

function LangCell({ flag, lang, type, value, onChange }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span aria-hidden>{flag}</span>
        <span className="font-display font-semibold text-xs text-white/70">{lang}</span>
      </div>

      {type === "text" && (
        <input className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      )}

      {type === "textarea" && (
        <textarea
          className="field"
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {type === "list" && (
        <textarea
          className="field"
          rows={Math.max(3, (Array.isArray(value) ? value.length : 0) + 1)}
          value={listToText(value)}
          onChange={(e) => onChange(textToList(e.target.value))}
          placeholder="Un elemento per riga"
        />
      )}

      {type === "list-td" && (
        <ListTDEditor value={Array.isArray(value) ? value : []} onChange={onChange} />
      )}
    </div>
  );
}

function ListTDEditor({ value, onChange }) {
  const update = (idx, key, v) => {
    const next = value.map((it, i) => (i === idx ? { ...it, [key]: v } : it));
    onChange(next);
  };
  const add = () => onChange([...value, { t: "", d: "" }]);
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...value];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {value.map((it, idx) => (
        <div key={idx} className="border border-white/10 p-3 space-y-2 bg-white/[0.02]">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-display tracking-[0.18em] uppercase text-[color:var(--gold)]">#{idx + 1}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => moveUp(idx)} className="text-white/50 hover:text-white text-xs">↑</button>
              <button type="button" onClick={() => remove(idx)} className="text-white/50 hover:text-[color:var(--fire)] p-1">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <input className="field !py-2" placeholder="Titolo" value={it.t || ""} onChange={(e) => update(idx, "t", e.target.value)} />
          <textarea className="field !py-2" rows={2} placeholder="Descrizione" value={it.d || ""} onChange={(e) => update(idx, "d", e.target.value)} />
        </div>
      ))}
      <button type="button" onClick={add} className="btn-ghost !py-2 !px-3 text-xs">
        <Plus className="w-3.5 h-3.5" /> Aggiungi voce
      </button>
    </div>
  );
}
